/// <amd-module name="Build/Run/LegacyRunTabProviderService" />
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { BuildCallbackType, IBuildResultsViewExtensionConfig } from "Build/Common/Library/ContributedExtension.types";
import { IRunViewContributedTabProps } from 'Build/Run/RunViewContributedTab';
import { ObservableArray } from 'VSS/Core/Observable';
import { MakeOptional } from "VSS/Core/Util/Types";
import { getContributedItems } from "VSS/Features/PlatformUI/ContributedItemUtil";
import { processTabContribution } from "VSS/Features/PlatformUI/ContributedTab";
import { ITabService } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssPageContext, Services, setRemoteSerializationSettings, VssService } from 'VSS/Platform/Context';
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssContributedTab } from "VSSUI/Tabs";
import { legacyNoop } from "./RunView";

interface ILegacyContribution extends IVssContributedTab {
    supportsTasks: string[];
}

export class LegacyRunTabProviderService extends VssService implements ITabService<IRunViewContributedTabProps> {
    private _tabs: ObservableArray<IVssContributedTab> = new ObservableArray();
    private _data: IRunViewContributedTabProps;
    private _tabMap: { [key: string]: boolean } = {};
    private _tabSupportsTasks: { [key: string]: string[] } = {};
    private _client: IBuildRestClient;

    private _extCallbacks: BuildCallbackType[] = [];

    public getTabs(id: string, data: IRunViewContributedTabProps) {
        this._data = data;
        if (this._data) {
            this._initialize();
            this._data.status.subscribe(this._initialize);
        }
        return this._tabs;
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._client = pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
    }

    public _serviceEnd(pageContext: IVssPageContext): void {
        super._serviceEnd(pageContext);
        if (this._data) {
            this._data.status.unsubscribe(this._initialize);
        }
    }

    private _initialize = async () => {
        if (this._data) {
            const legacyTabs = getContributedItems<ILegacyContribution>(this.pageContext, "ms.vss-build-web.build-results-view", undefined, "ms.vss-build-web.build-results-tab");
            const tabs: IVssContributedTab[] = [];
            legacyTabs.forEach(x => {
                const contribution = x.contribution;
                if (contribution.supportsTasks && contribution.supportsTasks.length > 0) {
                    this._tabSupportsTasks[x.id] = contribution.supportsTasks.map(x => x.toLowerCase());
                }

                tabs.push(processTabContribution(this.pageContext, {
                    contextualData: this._getLegacyConfig(),
                    props: this._getLegacyConfig(),
                    preProcessTabContribution: this._updateLegacyTab
                }, {
                        id: x.id,
                        ...(x.contribution as MakeOptional<ILegacyContribution, "id">)
                    }, x.id));
            });

            const tasksIdsUsed: string[] = [];
            if (Object.keys(this._tabSupportsTasks).length > 0) {
                const timeline = await this._client.getBuildTimeline(this._data.projectId.value, this._data.runId.value);
                const tasks = (timeline && timeline.records || []).filter(x => x.type === "Task");
                tasks.forEach(x => {
                    let id = x.task && x.task.id;
                    if (!id) {
                        id = x.id;
                    }
                    tasksIdsUsed.push(id.toLowerCase());
                });
            }

            const tabsToPush: IVssContributedTab[] = [];
            const tabIdsToRemove: string[] = [];
            tabs.forEach(x => {
                const supportsTasks = this._tabSupportsTasks[x.id];
                let renderTab = true;
                if (supportsTasks) {
                    renderTab = false;
                    for (let index = 0; index < supportsTasks.length; index++) {
                        const supportTaskId = supportsTasks[index];
                        if (tasksIdsUsed.indexOf(supportTaskId) > -1) {
                            // found atleast one
                            renderTab = true;
                            break;
                        }
                    }
                }

                if (renderTab && !this._tabMap[x.id]) {
                    this._tabMap[x.id] = true;
                    tabsToPush.push(x);
                }
                if (!renderTab && this._tabMap[x.id]) {
                    delete this._tabMap[x.id];
                    tabIdsToRemove.push(x.id);
                }
            });

            if (tabsToPush.length > 0) {
                this._tabs.push(...tabsToPush);
            }
            if (tabIdsToRemove.length > 0) {
                this._tabs.removeAll(x => tabIdsToRemove.indexOf(x.id) > -1);
            }
        }
    }

    private _getLegacyConfig() {
        const config: IBuildResultsViewExtensionConfig =  {
            onBuildChanged: (handler: BuildCallbackType) => {
                this._extCallbacks.push(handler);
                const latestBuild = this._getBuild();
                latestBuild && latestBuild.then(x => handler(x));
            },
            onViewDisplayed: legacyNoop,
            selectTab: legacyNoop,
            setSectionVisibility: legacyNoop
        };

        setRemoteSerializationSettings(config, { proxyFunctions: true });

        return config;
    }

    private _updateLegacyTab = (tab: IVssContributedTab) => {
        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        tab.url = historyService.generateUrl({ "view": tab.id });
        // push legacy tabs to far end
        if (tab.order !== undefined) {
            tab.order = 100 + tab.order;
        }
    }

    private _getBuild() {
        return this._client.getBuild(this._data.projectId.value, this._data.runId.value);
    }
}

Services.add("legacy-run-tab-provider-service", { serviceFactory: LegacyRunTabProviderService });
